import axios from 'axios';

const CERTIFICATE_CATEGORY_PREFIX = 'RECEIVING_CERTIFICATE';
const LOG_CARD_CATEGORY_PREFIX = 'LOG_CARD';

/** Inline receiving upload types shown in the receiving form. */
export const RECEIVING_LINE_DOCUMENT_TYPES = [
  { value: 'COC', label: 'CoC', categoryPrefix: CERTIFICATE_CATEGORY_PREFIX },
  { value: 'LOG_CARD', label: 'Log Card', categoryPrefix: LOG_CARD_CATEGORY_PREFIX },
  { value: 'OTHER', label: 'Other', categoryPrefix: 'OTHER' }
];

export function buildReceivingDocumentCategory(docType, partNumber = '') {
  const type = RECEIVING_LINE_DOCUMENT_TYPES.find((entry) => entry.value === docType)
    || RECEIVING_LINE_DOCUMENT_TYPES[0];
  const pn = String(partNumber || '').trim();
  return pn ? `${type.categoryPrefix}:${pn}` : type.categoryPrefix;
}

export function getReceivingDocumentTypeLabel(category) {
  const cat = String(category || '').trim().toUpperCase();
  if (cat === CERTIFICATE_CATEGORY_PREFIX || cat.startsWith(`${CERTIFICATE_CATEGORY_PREFIX}:`)) {
    return 'CoC';
  }
  if (cat === LOG_CARD_CATEGORY_PREFIX || cat.startsWith(`${LOG_CARD_CATEGORY_PREFIX}:`)) {
    return 'Log Card';
  }
  if (cat === 'OTHER' || cat.startsWith('OTHER:')) return 'Other';
  if (cat === 'AIRWORTHINESS_TAG') return 'Airworthiness Tag';
  if (cat === 'CALIBRATION_CERTIFICATE') return 'Calibration';
  return cat || 'Document';
}

export function isCertificateDocumentCategory(category) {
  const cat = String(category || '').trim().toUpperCase();
  return cat === CERTIFICATE_CATEGORY_PREFIX
    || cat.startsWith(`${CERTIFICATE_CATEGORY_PREFIX}:`)
    || cat === LOG_CARD_CATEGORY_PREFIX
    || cat.startsWith(`${LOG_CARD_CATEGORY_PREFIX}:`)
    || cat === 'AIRWORTHINESS_TAG'
    || cat === 'CALIBRATION_CERTIFICATE'
    || cat === 'INVENTORY_CERTIFICATE';
}

function normalizePartToken(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function documentNameMatchesPart(doc, partNumber) {
  const pn = normalizePartToken(partNumber);
  if (!pn) return false;
  const name = normalizePartToken(doc?.originalName || doc?.fileName || doc?.filename || '');
  return Boolean(name && name.includes(pn));
}

function categoryMatchesPart(category, partNumber) {
  const pn = String(partNumber || '').trim();
  if (!pn) return false;
  const cat = String(category || '');
  return cat === `${CERTIFICATE_CATEGORY_PREFIX}:${pn}`
    || cat === `${LOG_CARD_CATEGORY_PREFIX}:${pn}`
    || cat === `OTHER:${pn}`;
}

function sortDocumentsNewestFirst(documents) {
  return [...documents].sort((a, b) => {
    const ta = new Date(a?.uploadedAt || a?.createdAt || 0).getTime();
    const tb = new Date(b?.uploadedAt || b?.createdAt || 0).getTime();
    return tb - ta;
  });
}

/** All part-scoped receiving docs (CoC, Log Card, etc.), newest first. */
export function findReceivingCertificateDocuments(documents = [], partNumber = '') {
  const docs = Array.isArray(documents) ? documents : [];
  const pn = String(partNumber || '').trim();
  const certs = docs.filter((doc) => isCertificateDocumentCategory(doc?.category)
    || String(doc?.category || '').toUpperCase().startsWith('OTHER'));

  if (pn) {
    const exact = certs.filter((doc) => categoryMatchesPart(doc?.category, pn));
    if (exact.length) return sortDocumentsNewestFirst(exact);

    const byName = certs.filter((doc) => documentNameMatchesPart(doc, pn));
    if (byName.length) return sortDocumentsNewestFirst(byName);

    // Generic (unscoped) docs — only reuse when unambiguous so multi-line
    // receiving does not show the same file on every part row.
    const generic = certs.filter((doc) => {
      const cat = String(doc?.category || '').trim().toUpperCase();
      return cat === CERTIFICATE_CATEGORY_PREFIX
        || cat === LOG_CARD_CATEGORY_PREFIX
        || cat === 'OTHER'
        || cat === 'AIRWORTHINESS_TAG'
        || cat === 'CALIBRATION_CERTIFICATE'
        || cat === 'INVENTORY_CERTIFICATE';
    });
    if (generic.length === 1) return generic;
    return [];
  }

  return sortDocumentsNewestFirst(certs);
}

export function findReceivingCertificateDocument(documents = [], partNumber = '') {
  return pickLatestDocument(findReceivingCertificateDocuments(documents, partNumber));
}

export function findInventoryCertificateDocument(documents = [], partNumber = '') {
  const docs = Array.isArray(documents) ? documents : [];
  const certs = docs.filter((doc) => isCertificateDocumentCategory(doc?.category));
  const pool = certs.length ? certs : docs;
  const pn = String(partNumber || '').trim();
  if (pn) {
    const exact = pool.filter((doc) => categoryMatchesPart(doc?.category, pn)
      || String(doc?.category || '') === `${CERTIFICATE_CATEGORY_PREFIX}:${pn}`);
    if (exact.length) return pickLatestDocument(exact);
    const byName = pool.filter((doc) => documentNameMatchesPart(doc, pn));
    if (byName.length) return pickLatestDocument(byName);
  }
  return pickLatestDocument(pool);
}

function pickLatestDocument(documents) {
  if (!documents.length) return null;
  return [...documents].sort((a, b) => {
    const ta = new Date(a?.uploadedAt || a?.createdAt || 0).getTime();
    const tb = new Date(b?.uploadedAt || b?.createdAt || 0).getTime();
    return tb - ta;
  })[0];
}

export function getDocumentDisplayName(doc) {
  if (!doc) return '';
  return doc.originalName || doc.fileName || doc.filename || 'certificate';
}

export function downloadLocalFile(file, filename) {
  if (!file) return;
  const blobUrl = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename || file.name || 'certificate';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

async function messageFromDownloadError(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Failed to download document';
  try {
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || 'Failed to download document';
      } catch {
        return text || 'Failed to download document';
      }
    }
    if (typeof data === 'object') {
      return data.message || data.error || 'Failed to download document';
    }
    if (typeof data === 'string') return data;
  } catch {
    // ignore parse failures
  }
  return error?.message || 'Failed to download document';
}

export async function downloadStoreDocument({ basePath, entityId, doc, filename }) {
  if (!basePath || !entityId || !doc?._id) {
    throw new Error('Missing document download parameters');
  }
  try {
    const response = await axios.get(
      `${basePath}/${entityId}/documents/${doc._id}/download`,
      { responseType: 'blob' }
    );
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || getDocumentDisplayName(doc);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    const message = await messageFromDownloadError(error);
    const enriched = new Error(message);
    enriched.response = error.response;
    enriched.cause = error;
    throw enriched;
  }
}

export async function downloadReceivingCertificate(recordId, doc, filename) {
  return downloadStoreDocument({
    basePath: '/api/store-support/receiving',
    entityId: recordId,
    doc,
    filename
  });
}

export async function downloadInventoryCertificate(inventoryId, doc, filename, inventoryDomain = 'STORE') {
  const basePath = '/api/store-support/inventory';
  return downloadStoreDocument({
    basePath,
    entityId: inventoryId,
    doc,
    filename
  });
}
